import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { addBookingAddon, cancelBooking, fetchAllowedAddons, listMyBookings, payBooking, type AllowedAddonItem, type BookingWithSlotItem, type PayBookingBody } from '../widget-api/bookings';
import { ApiError } from '../widget-api/http-client';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useAsync } from '../widget-hooks/useAsync';
import { usePDTransferConfirm } from '../widget-hooks/use-pd-transfer-confirm';
import { bookingNeedsPayment } from '../widget-lib/booking-redirects';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { mergeWidgetQueryString } from '../widget-lib/merge-widget-query';
import { PATH, pathSession } from '../widget-lib/path-routes';
import { TabPills } from '../widget-ui/tab-pills';
import { PromoBanner } from '../widget-ui/promo-banner';
import { fetchScheduleSlotByID, type ScheduleSlotListItem } from '../widget-api/schedule-slots';
import { fetchSubscriptionsForSlot, type ForSlotSubscription } from '../widget-api/subscriptions';
import { resolvePaymentTTLMinutes } from '../widget-lib/payment-ttl';
import { BookingConfirmModal } from '../widget-ui/booking-confirm-modal';
import { bookingEndTs, bookingStatusBadgeClass, bookingStatusLabel } from '../widget-lib/booking-status';

function formatWhen(b: BookingWithSlotItem): string {
  try {
    const d = new Date(`${b.slot_date}T12:00:00`);
    const dateStr = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
    const start = (b.start_time || '').slice(0, 5);
    const end = b.end_time ? (b.end_time || '').slice(0, 5) : null;
    return end ? `${dateStr}, ${start}–${end}` : `${dateStr}, ${start}`;
  } catch {
    return `${b.slot_date} ${b.start_time}`;
  }
}

function formatWhenMobile(b: BookingWithSlotItem): string {
  try {
    const d = new Date(`${b.slot_date}T12:00:00`);
    const wd = d.toLocaleDateString('ru-RU', { weekday: 'short' });
    const dayMonth = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    const start = (b.start_time || '').slice(0, 5);
    const end = b.end_time ? (b.end_time || '').slice(0, 5) : null;
    const time = end ? `${start}–${end}` : start;
    return `${wd} ${dayMonth}  •  ${time}`;
  } catch {
    return formatWhen(b);
  }
}

type FilterTab = 'upcoming' | 'past' | 'all';
const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'upcoming', label: 'Предстоящие' },
  { key: 'past', label: 'Прошедшие' },
  { key: 'all', label: 'Все' },
];
const PAGE_SIZE = 10;

export default function BookingsPage() {
  useWidgetSeo(
    'Мои записи',
    'Список ваших записей на тренировки и индивидуальные занятия, статус оплаты и управление записями.'
  );
  const suffix = useQuerySuffix();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken, me, effectiveModules, bootstrapHint, orgSlug, organizationId, widgetContour, widgetConfig, guestShowcaseOrg, reloadBookings } = useEmbedBootstrap();
  const showQrFeature = (effectiveModules ?? me?.modules)?.qrcode === true;
  const isMobile = widgetContour === 'mobile';
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [filterTab, setFilterTab] = useState<FilterTab>('upcoming');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [payingBooking, setPayingBooking] = useState<BookingWithSlotItem | null>(null);
  const [payingSlot, setPayingSlot] = useState<ScheduleSlotListItem | null>(null);
  const [payingSlotSubs, setPayingSlotSubs] = useState<ForSlotSubscription[]>([]);
  const [payingPaymentMode, setPayingPaymentMode] = useState<'online' | 'subscription' | 'cash'>('online');
  const [payingSelectedSubId, setPayingSelectedSubId] = useState<string>('');
  const [payLoading, setPayLoading] = useState(false);
  const [selectedDetailBooking, setSelectedDetailBooking] = useState<BookingWithSlotItem | null>(null);
  const autoOpenHandledRef = useRef<string | null>(null);
  const [addingFor, setAddingFor] = useState<number | null>(null);
  const [addingOptions, setAddingOptions] = useState<AllowedAddonItem[]>([]);
  const [addingLoading, setAddingLoading] = useState(false);
  const [addingBusyId, setAddingBusyId] = useState<number | null>(null);

  // Ф8 (ТЗ 5.14 п.4): уведомление А.3 и галочка на шаге онлайн-оплаты.
  const pdTransfer = usePDTransferConfirm({
    accessToken,
    organizationId,
    slotId: payingBooking?.slot_id ?? null,
    enabled: !!payingBooking && payingPaymentMode === 'online'
  });

  const cashAtStudioEnabled = useMemo(
    () =>
      widgetConfig?.client_booking_cash_at_studio_enabled === true ||
      guestShowcaseOrg?.client_booking_cash_at_studio_enabled === true,
    [widgetConfig?.client_booking_cash_at_studio_enabled, guestShowcaseOrg?.client_booking_cash_at_studio_enabled]
  );

  const paymentTTLMinutes = useMemo(
    () => resolvePaymentTTLMinutes(widgetConfig, guestShowcaseOrg),
    [widgetConfig, guestShowcaseOrg]
  );

  const selectedSubscription = useMemo(() => {
    if (payingPaymentMode !== 'subscription' || !payingSlotSubs) return null;
    const sid = parseInt(payingSelectedSubId, 10);
    return payingSlotSubs.find((s) => s.id === sid) ?? null;
  }, [payingPaymentMode, payingSelectedSubId, payingSlotSubs]);

  const startPayment = useCallback(async (b: BookingWithSlotItem) => {
    if (!accessToken) return;
    setPayingBooking(b);
    setPayLoading(true);
    setActionError(null);
    try {
      const [slotData, subsData] = await Promise.all([
        fetchScheduleSlotByID(b.slot_id, accessToken),
        fetchSubscriptionsForSlot(b.slot_id, accessToken)
      ]);
      setPayingSlot(slotData);
      setPayingSlotSubs(subsData);
      if (subsData.length > 0) {
        setPayingPaymentMode('subscription');
        setPayingSelectedSubId(String(subsData[0].id));
      } else {
        setPayingPaymentMode('online');
        setPayingSelectedSubId('');
      }
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Не удалось загрузить данные для оплаты');
      setPayingBooking(null);
    } finally {
      setPayLoading(false);
    }
  }, [accessToken]);

  async function handleConfirmPayment() {
    if (!payingBooking || !accessToken) return;
    setPayLoading(true);
    setActionError(null);
    try {
      const body: PayBookingBody = {
        payment_mode: payingPaymentMode,
        user_subscription_id: payingPaymentMode === 'subscription' ? parseInt(payingSelectedSubId, 10) : null,
        return_url: `${window.location.origin}/embed${PATH.bookings}${suffix}`,
        ...(payingPaymentMode === 'online' ? pdTransfer.payload : {})
      };
      const res = await payBooking(payingBooking.id, body, accessToken, organizationId);
      if (res.payment_url) {
        window.location.assign(res.payment_url);
        return;
      }
      setPayingBooking(null);
      setPayingSlot(null);
      reload();
      reloadBookings();
    } catch (e) {
      if (pdTransfer.applyServerError(e)) {
        // Список получателей успел измениться — показываем свежий текст и
        // требуем повторную галочку (ТЗ 5.14 п.5, без 409).
      }
      setActionError(e instanceof Error ? e.message : 'Не удалось провести оплату');
    } finally {
      setPayLoading(false);
    }
  }

  const filterMinorId = useMemo(() => {
    const raw = searchParams.get('minor_id');
    if (!raw) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const { data: bookings, loading, error, reload } = useAsync(
    () => {
      if (!accessToken) return Promise.resolve([]);
      return listMyBookings(accessToken, organizationId);
    },
    [accessToken, organizationId]
  );

  const scopedBookings = useMemo(() => {
    const rows = bookings ?? [];
    if (!filterMinorId) return rows;
    return rows.filter((b) => b.represented_minor_id === filterMinorId);
  }, [bookings, filterMinorId]);

  const filterMinorLabel = useMemo(() => {
    if (!filterMinorId) return null;
    const hit = scopedBookings.find((b) => b.represented_minor_id === filterMinorId && b.represented_minor_name);
    return hit?.represented_minor_name?.trim() || `подопечный #${filterMinorId}`;
  }, [filterMinorId, scopedBookings]);

  const bookingsClearFilterQs = mergeWidgetQueryString(searchParams.toString(), { minor_id: null });

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const u: BookingWithSlotItem[] = [];
    const p: BookingWithSlotItem[] = [];
    for (const b of scopedBookings) {
      const end = bookingEndTs(b);
      const isPast =
        b.status === 'cancelled' ||
        b.status === 'completed' ||
        b.status === 'no_show' ||
        end < now;
      (isPast ? p : u).push(b);
    }
    u.sort((a, b) => bookingEndTs(a) - bookingEndTs(b));
    p.sort((a, b) => bookingEndTs(b) - bookingEndTs(a));
    return { upcoming: u, past: p };
  }, [scopedBookings]);

  const filteredBookings = useMemo(() => {
    if (filterTab === 'upcoming') return upcoming;
    if (filterTab === 'past') return past;
    return [...upcoming, ...past];
  }, [filterTab, upcoming, past]);

  const visibleBookings = filteredBookings.slice(0, visibleCount);
  const hasMore = visibleCount < filteredBookings.length;
  const deepLinkBookingId = useMemo(() => {
    const raw = searchParams.get('booking_id');
    if (!raw) return null;
    const id = Number.parseInt(raw, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [searchParams]);
  const deepLinkWantsPay = searchParams.get('pay') === '1';

  useEffect(() => {
    if (!deepLinkWantsPay || deepLinkBookingId == null || loading || !accessToken || !bookings) {
      return;
    }
    const key = `${deepLinkBookingId}:pay`;
    if (autoOpenHandledRef.current === key) {
      return;
    }
    autoOpenHandledRef.current = key;
    const target = bookings.find((booking) => booking.id === deepLinkBookingId) ?? null;
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete('booking_id');
    nextParams.delete('pay');
    void setSearchParams(nextParams, { replace: true });
    if (target && bookingNeedsPayment(target)) {
      void startPayment(target);
      return;
    }
    setActionError('Эта запись уже оплачена или недоступна для повторной оплаты.');
  }, [accessToken, bookings, deepLinkBookingId, deepLinkWantsPay, loading, searchParams, setSearchParams, startPayment]);

  function handleTabChange(idx: number) {
    setFilterTab(FILTER_TABS[idx].key);
    setVisibleCount(PAGE_SIZE);
  }

  async function onCancel(b: BookingWithSlotItem) {
    if (!accessToken) return;
    setActionError(null);
    setCancellingId(b.id);
    try {
      await cancelBooking(b.id, accessToken, organizationId);
      reload();
      reloadBookings();
    } catch (e) {
      if (e instanceof ApiError) {
        setActionError(e.detail);
      } else {
        setActionError(e instanceof Error ? e.message : 'Не удалось отменить');
      }
    } finally {
      setCancellingId(null);
    }
  }

  if (bootstrapHint && !orgSlug) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Мои записи</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Мои записи</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы увидеть записи.</p>
        <Link
          to={`${PATH.signIn}${suffix}`}
          className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]"
        >
          Войти
        </Link>
      </div>
    );
  }

  async function openAddonPicker(b: BookingWithSlotItem) {
    if (!organizationId) {
      setActionError('Неизвестна организация для подбора допов.');
      return;
    }
    if (addingFor === b.id) {
      setAddingFor(null);
      return;
    }
    setAddingFor(b.id);
    setAddingLoading(true);
    setActionError(null);
    try {
      const res = await fetchAllowedAddons(b.service_id, organizationId, null, b.user_subscription_id ?? null);
      const taken = new Set((b.addons ?? []).map((a) => a.addon_service_id));
      setAddingOptions(res.items.filter((a) => !taken.has(a.id)));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Не удалось загрузить допы');
      setAddingFor(null);
    } finally {
      setAddingLoading(false);
    }
  }

  async function confirmAddAddon(b: BookingWithSlotItem, addonId: number) {
    if (!accessToken) return;
    setAddingBusyId(addonId);
    setActionError(null);
    try {
      const useSub = b.payment_method === 'subscription' && b.user_subscription_id != null;
      const res = await addBookingAddon(
        b.id,
        useSub
          ? { addon_service_id: addonId, payment_method: 'subscription', user_subscription_id: b.user_subscription_id }
          : {
              addon_service_id: addonId,
              payment_method: 'online',
              return_url: `${window.location.origin}/embed${PATH.bookings}${suffix}`
            },
        accessToken
      );
      if (res.payment_url) {
        window.location.assign(res.payment_url);
        return;
      }
      setAddingFor(null);
      reload();
      reloadBookings();
    } catch (e) {
      if (e instanceof ApiError) {
        setActionError(e.detail);
      } else {
        setActionError(e instanceof Error ? e.message : 'Не удалось добавить доп');
      }
    } finally {
      setAddingBusyId(null);
    }
  }

  function renderBookingCard(b: BookingWithSlotItem) {
    const isUnpaid = bookingNeedsPayment(b);
    return (
      <li key={b.id}>
        <div className={`overflow-hidden rounded-2xl border bg-[var(--bg-elevated)] shadow-[var(--shadow-card)] transition-all duration-200 ${
          isUnpaid ? 'border-amber-300 dark:border-amber-900/50' : 'border-[var(--border)]'
        }`}>
          <div
            onClick={() => setSelectedDetailBooking(b)}
            className="block space-y-1.5 p-4 transition-colors cursor-pointer hover:bg-[var(--bg-soft)]"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedDetailBooking(b);
              }
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-bold text-[var(--text)] flex items-center gap-1.5">
                <span>{b.service_name}</span>
                <span className="text-xs text-[var(--text-muted)] font-normal opacity-60">›</span>
              </p>
              <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                {isUnpaid && b.status === 'pending_payment' ? null : (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${bookingStatusBadgeClass(b.status)}`}>
                    <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                    {bookingStatusLabel(b.status)}
                  </span>
                )}
                {isUnpaid ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200 px-2 py-0.5 text-[11px] font-semibold">
                    <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                    Не оплачено
                  </span>
                ) : null}
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              {isMobile ? formatWhenMobile(b) : formatWhen(b)}
            </p>
            {b.represented_minor_name ? (
              <p className="text-xs text-[var(--text-muted)]">Для: {b.represented_minor_name}</p>
            ) : null}
            {isUnpaid ? (
              <div className="mt-2 rounded-xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/30 p-3 text-[12px] text-amber-800 dark:text-amber-200 leading-relaxed">
                Запись подтверждена тренером, но ожидает оплаты. Вы можете оплатить занятие онлайн или списать с абонемента.
              </div>
            ) : null}
            {(b.addons ?? []).length > 0 ? (
              <div className="mt-1 space-y-0.5">
                {(b.addons ?? []).map((a) => (
                  <p key={a.id} className="text-xs text-[var(--text-muted)]">
                    + {a.addon_name}
                    {a.price_cents > 0 ? ` — ${Math.round(a.price_cents / 100).toLocaleString('ru-RU')} ₽` : ' — бесплатно'}
                  </p>
                ))}
              </div>
            ) : null}
            {b.comment?.trim() ? (
              <p className="text-xs text-[var(--text-muted)]">{b.comment.trim()}</p>
            ) : null}
          </div>
          {addingFor === b.id ? (
            <div className="border-t border-[var(--border)] px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
              {addingLoading ? (
                <p className="text-xs text-[var(--text-muted)]">Загружаем допы…</p>
              ) : addingOptions.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">Нет доступных допов.</p>
              ) : (
                <ul className="space-y-2">
                  {addingOptions.map((a) => {
                    const byPlan = b.payment_method === 'subscription' && a.covered_by_plan === true;
                    return (
                      <li key={a.id} className="flex items-center gap-2 text-sm">
                        <span className="min-w-0 flex-1 text-[var(--text)]">
                          {a.name}
                          {byPlan ? (
                            <span className="ml-1.5 inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
                              входит в абонемент
                            </span>
                          ) : null}
                          <span className="ml-1 text-[var(--text-muted)]">
                            {byPlan
                              ? '0 ₽ по абонементу'
                              : Number(a.price) > 0
                                ? `+${Math.round(Number(a.price)).toLocaleString('ru-RU')} ₽`
                                : 'бесплатно'}
                          </span>
                        </span>
                        <Button
                          type="button"
                          className={isMobile ? 'min-h-9 px-4' : undefined}
                          disabled={addingBusyId === a.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            void confirmAddAddon(b, a.id);
                          }}
                        >
                          {addingBusyId === a.id ? '…' : 'Добавить'}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
          {b.can_cancel || bookingNeedsPayment(b) || b.status !== 'cancelled' ? (
            <div className="border-t border-[var(--border)] px-4 py-2.5 flex justify-end gap-2">
              {b.status !== 'cancelled' ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    void openAddonPicker(b);
                  }}
                >
                  {addingFor === b.id ? 'Скрыть допы' : '+ Доп'}
                </Button>
              ) : null}
              {bookingNeedsPayment(b) ? (
                <Button
                  type="button"
                  disabled={payLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    void startPayment(b);
                  }}
                >
                  Оплатить
                </Button>
              ) : null}
              {b.can_cancel ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={cancellingId === b.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    void onCancel(b);
                  }}
                >
                  {cancellingId === b.id ? 'Отмена…' : 'Отменить запись'}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </li>
    );
  }

  const activeTabIdx = FILTER_TABS.findIndex((t) => t.key === filterTab);

  return (
    <div className="space-y-4 w-full min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className={isMobile ? 'text-2xl font-bold tracking-tight text-[var(--text)]' : 'text-xl font-semibold tracking-tight'}>
          Мои записи
        </h1>
      </div>

      <PromoBanner screen="my_bookings" />

      {filterMinorId ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2.5 text-sm shadow-[var(--shadow-card)]">
          <span className="text-[var(--text-muted)]">
            Записи: <span className="font-semibold text-[var(--text)]">{filterMinorLabel}</span>
          </span>
          <Link
            className="shrink-0 text-sm font-medium text-[var(--accent)] underline-offset-2 hover:underline"
            to={`${PATH.bookings}${bookingsClearFilterQs}`}
          >
            Все записи
          </Link>
        </div>
      ) : null}

      <TabPills
        items={FILTER_TABS.map((t, i) => ({ label: t.label, active: i === activeTabIdx }))}
        onChange={handleTabChange}
      />

      {actionError ? (
        <p className="text-sm text-[var(--error-text)]" role="alert">{actionError}</p>
      ) : null}

      {loading ? (
        <ul className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="h-24 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
          ))}
        </ul>
      ) : error ? (
        <p className="text-sm text-[var(--error-text)]">{error}</p>
      ) : filteredBookings.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/50 px-4 py-10 text-center shadow-[var(--shadow-card)]">
          <p className="text-sm text-[var(--text-muted)]">
            {filterTab === 'upcoming' && past.length > 0
              ? 'Нет предстоящих записей.'
              : filterTab === 'past' && upcoming.length > 0
                ? 'Нет прошедших записей.'
                : filterMinorId && (bookings?.length ?? 0) > 0
                  ? 'Для этого подопечного записей пока нет.'
                  : 'Пока нет записей. Откройте расписание и запишитесь.'}
          </p>
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {visibleBookings.map((b) => renderBookingCard(b))}
          </ul>
          {hasMore ? (
            <div className="text-center">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
              >
                Показать ещё
              </Button>
            </div>
          ) : null}
        </>
      )}

      {payingBooking && payingSlot && (
        <BookingConfirmModal
          open={!!payingBooking && !!payingSlot}
          onClose={() => {
            setPayingBooking(null);
            setPayingSlot(null);
          }}
          onConfirm={handleConfirmPayment}
          loading={payLoading}
          slot={payingSlot}
          paymentMode={payingPaymentMode}
          selectedSubscription={selectedSubscription}
          slotSubs={payingSlotSubs}
          cashAtStudioEnabled={cashAtStudioEnabled}
          serviceNeedsPayment={true}
          paymentTTLMinutes={paymentTTLMinutes}
          onSelectPaymentMode={(mode) => setPayingPaymentMode(mode)}
          onSelectSubscription={(subId) => setPayingSelectedSubId(subId)}
          pdTransferNotice={pdTransfer.notice}
          pdTransferLoading={pdTransfer.loading}
          pdTransferError={pdTransfer.error}
          pdTransferConfirmed={pdTransfer.confirmed}
          onPdTransferConfirmedChange={pdTransfer.setConfirmed}
        />
      )}

      {selectedDetailBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-6 shadow-2xl space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-[var(--text)]">{selectedDetailBooking.service_name}</h3>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">{formatWhen(selectedDetailBooking)}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedDetailBooking(null)}
                className="text-[var(--text-muted)] hover:text-[var(--text)] text-lg leading-none"
              >
                ✕
              </button>
            </div>

            <div className="space-y-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-3 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-[var(--text-muted)]">Статус:</span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${bookingStatusBadgeClass(selectedDetailBooking.status)}`}>
                  {bookingStatusLabel(selectedDetailBooking.status)}
                </span>
              </div>
              {selectedDetailBooking.represented_minor_name && (
                <div className="flex justify-between items-center">
                  <span className="text-[var(--text-muted)]">Запись для:</span>
                  <span className="font-medium text-[var(--text)]">{selectedDetailBooking.represented_minor_name}</span>
                </div>
              )}
              {selectedDetailBooking.comment?.trim() && (
                <div className="pt-1 border-t border-[var(--border)]">
                  <span className="text-[var(--text-muted)]">Комментарий:</span>
                  <p className="mt-0.5 text-[var(--text)]">{selectedDetailBooking.comment.trim()}</p>
                </div>
              )}
            </div>

            {bookingNeedsPayment(selectedDetailBooking) && (
              <div className="rounded-xl bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900/40 p-3 text-xs text-amber-800 dark:text-amber-200">
                Запись ожидаем оплаты. Вы можете оплатить онлайн или предъявить абонемент в студии.
              </div>
            )}

            <div className="flex flex-col gap-2 pt-2">
              {bookingNeedsPayment(selectedDetailBooking) && (
                <Button
                  type="button"
                  disabled={payLoading}
                  onClick={() => {
                    const b = selectedDetailBooking;
                    setSelectedDetailBooking(null);
                    void startPayment(b);
                  }}
                >
                  Оплатить запись
                </Button>
              )}

              {showQrFeature && (
                <Link
                  to={`${PATH.myAccess}${suffix}`}
                  onClick={() => setSelectedDetailBooking(null)}
                  className="inline-flex h-9 items-center justify-center rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 text-xs font-medium text-[var(--text)] hover:bg-[var(--bg-hover)] transition-colors"
                >
                  📱 Мой QR-код прохода на турникете
                </Link>
              )}

              <Link
                to={`${pathSession(selectedDetailBooking.slot_id)}${suffix}`}
                onClick={() => setSelectedDetailBooking(null)}
                className="inline-flex h-9 items-center justify-center rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              >
                Информация о занятии ›
              </Link>

              {selectedDetailBooking.can_cancel && (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={cancellingId === selectedDetailBooking.id}
                  onClick={() => {
                    const b = selectedDetailBooking;
                    setSelectedDetailBooking(null);
                    void onCancel(b);
                  }}
                >
                  Отменить запись
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
